/**
 * Full screen for the whole page. While full screen, the Keyboard Lock API (Chromium) keeps Esc
 * for the game, so Esc still opens the pause menu; holding Esc or "Exit full screen" leaves it.
 * Browsers without these APIs stay windowed.
 */
type KeyboardLock = { lock?: (keys: string[]) => Promise<void>; unlock?: () => void };
const keyboard = () => (navigator as Navigator & { keyboard?: KeyboardLock }).keyboard;

export const isFullscreen = (): boolean => !!document.fullscreenElement;

/** Must run inside a user gesture (a click or a key press). */
export async function enterFullscreen(): Promise<void> {
  if (isFullscreen() || typeof document.documentElement.requestFullscreen !== 'function') return;
  try {
    await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
    await keyboard()?.lock?.(['Escape']);
  } catch { /* refused: stay windowed */ }
}

export async function exitFullscreen(): Promise<void> {
  if (!isFullscreen()) return;
  keyboard()?.unlock?.();
  try { await document.exitFullscreen(); } catch { /* already left */ }
}

export const toggleFullscreen = (): Promise<void> => (isFullscreen() ? exitFullscreen() : enterFullscreen());

export function onFullscreenChange(handler: (fullscreen: boolean) => void): () => void {
  const h = () => handler(isFullscreen());
  document.addEventListener('fullscreenchange', h);
  return () => document.removeEventListener('fullscreenchange', h);
}
